import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import type { ReactNode } from "react";

const faqAnswerLinkSx = {
  fontWeight: 600,
  color: "primary.main",
  "&::before": { display: "none" },
};

const answerStackSx = {
  typography: "body2",
  color: "text.secondary",
  "& strong": {
    fontWeight: 700,
    color: "text.primary",
  },
};

const FAQ_ITEMS: { q: string; a: ReactNode }[] = [
  {
    q: "How do I sign in?",
    a: (
      <>
        Enter the email or mobile number registered with your seller account, tap <strong>Get OTP</strong>, and enter
        the <strong>4-digit code</strong> we send you. <strong>No password</strong> is required for the OTP flow.
      </>
    ),
  },
  {
    q: "Who can register as a partner?",
    a: (
      <>
        If you run a food business—such as a <strong>restaurant, cloud kitchen, bakery, café, or juice shop</strong>
        —you can apply to join Tazty.
        <br />
        <br />
        We onboard partners who meet our <strong>quality, compliance, and operational standards</strong>.
        <br />
        <br />
        Tap <strong>&ldquo;Apply to Partner&rdquo;</strong> on the login screen to begin registration.
      </>
    ),
  },
  {
    q: "What documents and details are required for registration?",
    a: (
      <Stack spacing={1.5} sx={answerStackSx}>
        <Typography variant="body2" color="text.secondary" component="p" sx={{ m: 0 }}>
          To register as a <strong>Tazty partner</strong>, you&apos;ll need to provide the following:
        </Typography>
        <Box
          component="ul"
          sx={{
            m: 0,
            pl: 2.5,
            mb: 0,
            listStyleType: "disc",
            "& li": { display: "list-item", mb: 0.5 },
          }}
        >
          <Typography component="li" variant="body2" color="text.secondary" sx={{ display: "list-item" }}>
            <strong>FSSAI license</strong> (mandatory for all food businesses)
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary" sx={{ display: "list-item" }}>
            <strong>PAN details</strong> (for business or owner)
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary" sx={{ display: "list-item" }}>
            <strong>Bank account details</strong> (for payouts)
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary" sx={{ display: "list-item" }}>
            <strong>Menu details</strong> (items, pricing, and descriptions)
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary" sx={{ display: "list-item" }}>
            <strong>Product images</strong> (clear photos of your dishes)
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" component="p" sx={{ m: 0 }}>
          <strong>GST details</strong> are optional and can be provided if applicable to your business.
        </Typography>
      </Stack>
    ),
  },
  {
    q: "Is there any onboarding or registration fee to go live on Tazty?",
    a: (
      <Stack spacing={1.5} sx={answerStackSx}>
        <Typography variant="body2" color="text.secondary" component="p" sx={{ m: 0 }}>
          <strong>No—joining Tazty is completely free.</strong> There are <strong>no onboarding or registration charges</strong>.
        </Typography>
        <Typography variant="body2" color="text.secondary" component="p" sx={{ m: 0 }}>
          Any commissions or fees will be <strong>transparently shared</strong> during the onboarding process.
        </Typography>
      </Stack>
    ),
  },
  {
    q: "How long does it take for a restaurant to go live on Tazty after submitting documents?",
    a: (
      <Stack spacing={1.5} sx={answerStackSx}>
        <Typography variant="body2" color="text.secondary" component="p" sx={{ m: 0 }}>
          Once you upload all required documents and accept the contract, our team typically completes verification and
          menu setup within <strong>24 hours</strong>.
        </Typography>
        <Typography variant="body2" color="text.secondary" component="p" sx={{ m: 0 }}>
          Once approved, your restaurant will be <strong>live and ready to accept orders</strong>.
        </Typography>
        <Typography variant="body2" color="text.secondary" component="p" sx={{ m: 0 }}>
          If any documents need correction, we&apos;ll notify you—your <strong>go-live timeline</strong> will continue
          once the updated documents are approved.
        </Typography>
      </Stack>
    ),
  },
  {
    q: "How much commission will I be charged by Tazty?",
    a: (
      <Stack spacing={1.5} sx={answerStackSx}>
        <Typography variant="body2" color="text.secondary" component="p" sx={{ m: 0 }}>
          Commission on Tazty depends on your <strong>restaurant type</strong> and the <strong>services you use</strong>{" "}
          (such as delivery, platform support, and operations).
        </Typography>
        <Typography variant="body2" color="text.secondary" component="p" sx={{ m: 0 }}>
          Your <strong>final commission rate</strong> will be shared upfront and can be viewed anytime in your{" "}
          <strong>Store Configuration</strong>—so you always know exactly what you&apos;re being charged.
        </Typography>
      </Stack>
    ),
  },
  {
    q: "How will I receive my settlements?",
    a: (
      <Stack spacing={1.5} sx={answerStackSx}>
        <Typography variant="body2" color="text.secondary" component="p" sx={{ m: 0 }}>
          Tazty follows an <strong>N+3 settlement cycle</strong>, meaning your earnings are credited to your bank account
          within <strong>3 working days</strong> after settlement processing.
        </Typography>
        <Box
          component="ul"
          sx={{
            m: 0,
            pl: 2.5,
            mb: 0,
            listStyleType: "disc",
            "& li": { display: "list-item", mb: 0.5 },
          }}
        >
          <Typography component="li" variant="body2" color="text.secondary" sx={{ display: "list-item" }}>
            An order becomes eligible for settlement <strong>1 day after it is delivered</strong>
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary" sx={{ display: "list-item" }}>
            Once eligible, the payout is processed as per the <strong>N+3 cycle</strong>
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary" sx={{ display: "list-item" }}>
            <strong>Bank holidays, Sundays, and local holidays are excluded</strong>, and settlements will be processed
            on the next working day
          </Typography>
        </Box>
      </Stack>
    ),
  },
  {
    q: "Where can I get help and support?",
    a: (
      <>
        Use any <strong>Social Links</strong> or <strong>WhatsApp</strong> from the header/footer to reach our team
        during <strong>business hours</strong>. Or else send us an email at{" "}
        <Link href="mailto:support@tazty.in" sx={faqAnswerLinkSx}>
          support@tazty.in
        </Link>{" "}
        or call us at{" "}
        <Link href="tel:+919952520699" sx={faqAnswerLinkSx}>
          +91 9952520699
        </Link>
      </>
    ),
  },
];

export interface PartnerLoginFaqDialogProps {
  open: boolean;
  onClose: () => void;
}

const PartnerLoginFaqDialog = ({ open, onClose }: PartnerLoginFaqDialogProps) => {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" scroll="paper">
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1 }}>
        <Typography component="span" variant="h6" fontWeight={800}>
          Frequently asked questions
        </Typography>
        <IconButton aria-label="Close FAQ" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 0 }}>
        {FAQ_ITEMS.map((item) => (
          <Accordion key={item.q} disableGutters elevation={0} sx={{ "&:before": { display: "none" } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={700}>{item.q}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={answerStackSx}>{item.a}</Box>
            </AccordionDetails>
          </Accordion>
        ))}
      </DialogContent>
    </Dialog>
  );
};

export default PartnerLoginFaqDialog;
